import React, { useEffect, useMemo, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";

import {
    Autocomplete,
    Box,
    Button,
    Container,
    FormControl,
    TextField,
    Typography,
} from "@mui/material";

import EventPredictionAI from "../components/EventPredictionAI";
import FiltersReport, { ITEM_HEIGHT } from "../components/FiltersReport";
import ReportBuilder, {
    BREAK_LINE,
    CUSTOM,
    GRID_HORIZONTAL,
    TAB,
    TABS,
} from "../components/shared/ReportBuilder/ReportBuilder";

import { setJS } from "../actions/globalReportFilters";

import {
    COMPARISON_SAME_TIME_LAST_YEAR,
    DATATYPE_CHECKIN,
    DATATYPE_RESDATE,
    DISPLAY_ELEMENT_HELPER,
    GROUP_COUNTRIES,
    GROUP_DATE_DAY,
    GROUP_DATE_MONTH,
    GROUP_DATE_YEAR,
    GROUP_OFFERS,
    PRESET_ANYTIME,
    PRESET_CUSTOM,
    PRESET_LAST_7_DAYS,
    PRESET_LAST_30_DAYS,
    PRESET_LAST_FULL_MONTH,
    PRESET_LAST_3_MONTHS,
    PRESET_LAST_6_MONTHS,
    PRESET_LAST_12_MONTHS,
    PRESET_LAST_YEAR,
    DATATYPE_RESDATE_NAME,
    DATATYPE_CHECKIN_NAME,
    GROUP_VOUCHERS,
    DEFAULT_COMPARISON,
} from "../helpers/filters";

import { dayjsUTC, MAX_DATE, MIN_DATE } from "../helpers/dates";
import { TARGET_OFF } from "../helpers/targets";
import { setTemplateKey } from "../actions/PDFExport";
import { REPORT_CHART_CATEGORY_STACKED } from "../components/shared/ReportBuilder/ReportChartCategoryStacked";
import { REPORT_CHART_PERFORMANCE_VS } from "../components/shared/ReportBuilder/ReportChartPerformanceVS";
import { REPORT_PIE_CHART } from "../components/shared/ReportBuilder/ReportPieChart";
import { REPORT_TABLE_METRICS_SUMMARY } from "../components/shared/ReportBuilder/ReportTableMetricsSummary";
import { ARO_ADMIN } from "../configuration";
import Icons from "../helpers/icons";
import EventsSummaryCard from "../components/EventsSummaryCard";
import { REPORT_TABLE_OVERVIEW } from "../components/shared/ReportBuilder/ReportTableOverview";
import { REPORT_CHART_BARS_CATEGORY } from "../components/shared/ReportBuilder/ReportChartBarsCategory";
import ReportChartBarsExtras from "../components/ReportChartBarsExtras";
import ReportMetricVSMetricBars from "../components/shared/ReportBuilder/ReportMetricVSMetricBars";

const dayjs = dayjsUTC;

const PRESET_EVENT_DATES = "Event Dates";

const METRICS = {
    revenueTotal: "Revenue",
    bookingsRooms: "Bookings",
    roomNightsTotal: "Room nights",
};

const REPORTS = [
    {
        type: REPORT_TABLE_METRICS_SUMMARY,
        groupBy: [],
        fieldsOrder: [
            "bookingsRooms",
            "roomNightsTotal",
            "ALoSTotal",
            "ADRTotal",
            "bookingsCancelled",
            "revenueCancelled",
            "cancellationRate",
            "revenueRoom",
            "revenueExtras",
            "revenueVouchers",
            "revenueTotal",
        ],
        hideFields: [
            ...Object.keys(DISPLAY_ELEMENT_HELPER).filter(
                (e) =>
                    ![
                        "bookingsRooms",
                        "roomNightsTotal",
                        "ALoSTotal",
                        "ADRTotal",
                        "bookingsCancelled",
                        "revenueCancelled",
                        "cancellationRate",
                        "revenueRoom",
                        "revenueExtras",
                        "revenueVouchers",
                        "revenueTotal",
                    ].includes(e),
            ),
        ],
        props: {
            place: "EventsPerformanceScreen",
        },
    },
    { type: BREAK_LINE, quantity: 1 },

    {
        type: CUSTOM,
        groupBy: [],
        title: "Event Summary",
        options: { duplicable: false },
        render: ({ js, hotelID, data }) => {
            const selectedEvent = js.selectedEvent ?? null;
            return (
                <EventsSummaryCard
                    mode="summary"
                    title="Event Summary"
                    event={selectedEvent}
                />
            );
        },
    },
    {
        type: TABS,
        default: "Revenue",
        tabs: Object.keys(METRICS).map((metric) => {
            return {
                type: TAB,
                title: METRICS[metric],
                reports: [
                    {
                        type: GRID_HORIZONTAL,
                        spacing: 2,
                        reports: [
                            {
                                type: REPORT_CHART_PERFORMANCE_VS,
                                groupBy: [
                                    GROUP_DATE_YEAR,
                                    GROUP_DATE_MONTH,
                                    GROUP_DATE_DAY,
                                ],
                                props: {
                                    title: "Event performance",
                                    metric: metric,
                                },
                            },
                            {
                                type: REPORT_PIE_CHART,
                                groupBy: [GROUP_COUNTRIES],
                                props: {
                                    title: "Event breakdown by Country",
                                    metric: metric,
                                },
                            },
                        ],
                    },
                    {
                        type: REPORT_CHART_CATEGORY_STACKED,
                        // filters: { Types: ["Special Offer"] },
                        groupBy: [
                            GROUP_OFFERS,
                            GROUP_DATE_YEAR,
                            GROUP_DATE_MONTH,
                            GROUP_DATE_DAY,
                        ],
                        props: {
                            title: "Top selling rates",
                            metric: metric,
                        },
                    },
                    {
                        type: REPORT_TABLE_OVERVIEW,
                        groupBy: [GROUP_OFFERS],
                        fieldsOrder: [metric],
                        hideFields: [
                            ...Object.keys(DISPLAY_ELEMENT_HELPER).filter(
                                (e) => !(e === metric),
                            ),
                        ],
                        props: {
                            title: "Top selling event rates",
                            emptyMessage:
                                "No rate plan data available for this selection",
                        },
                    },
                    ...(metric === "revenueTotal"
                        ? [
                              // Extras
                              {
                                  type: CUSTOM,
                                  groupBy: [],
                                  title: "Revenue by Extras",
                                  options: { duplicable: false },
                                  render: ({ data }) => (
                                      <ReportChartBarsExtras
                                          data={data}
                                          title="Revenue by Extras"
                                          initialMetric="totalCost"
                                          emptyMessage="No extras data available for this selection"
                                          metrics={{
                                              totalCost: {
                                                  label: "Total cost",
                                                  short: "Total revenue",
                                                  format: { left: "currency" },
                                                  important: true,
                                              },
                                          }}
                                      />
                                  ),
                              },
                              // Vouchers
                              {
                                  type: REPORT_CHART_BARS_CATEGORY,
                                  title: "Revenue by Vouchers",
                                  options: { duplicable: false },
                                  groupBy: [GROUP_VOUCHERS],
                                  metric: "revenueVouchers",
                                  metrics: {
                                      revenueVouchers: {
                                          ...DISPLAY_ELEMENT_HELPER.revenueVouchers,
                                      },
                                  },
                                  props: {
                                      title: "Revenue by Vouchers",
                                      showVouchers: true,
                                      emptyMessage:
                                          "No vouchers data available for this selection",
                                  },
                                  filters: { Types: ["Voucher"] },
                              },
                          ]
                        : metric === "bookingsRooms"
                          ? [
                                // Booking source distribution
                                // Semantic hack: bookingsNotMobile/bookingsMobile mapped to bookingsRooms
                                {
                                    type: CUSTOM,
                                    groupBy: [],
                                    title: "Booking source distribution",
                                    options: { duplicable: false },
                                    render: ({ data, js }) => {
                                        const r = data[""]?.result ?? {};
                                        const rc =
                                            data[""]?.resultOneYearBefore ?? {};
                                        return (
                                            <ReportMetricVSMetricBars
                                                emptyMessage="No booking source data available"
                                                data={{
                                                    Desktop: {
                                                        keys: [
                                                            [
                                                                "Country",
                                                                "Desktop",
                                                            ],
                                                        ],
                                                        result: {
                                                            bookingsRooms:
                                                                r.bookingsNotMobile ??
                                                                0,
                                                        },
                                                        resultOneYearBefore: {
                                                            bookingsRooms:
                                                                rc.bookingsNotMobile ??
                                                                0,
                                                        },
                                                    },
                                                    Mobile: {
                                                        keys: [
                                                            [
                                                                "Country",
                                                                "Mobile",
                                                            ],
                                                        ],
                                                        result: {
                                                            bookingsRooms:
                                                                r.bookingsMobile ??
                                                                0,
                                                        },
                                                        resultOneYearBefore: {
                                                            bookingsRooms:
                                                                rc.bookingsMobile ??
                                                                0,
                                                        },
                                                    },
                                                }}
                                                title="Booking source distribution"
                                                metrics={["bookingsRooms"]}
                                                aggregation="Country"
                                                showLastYear={true}
                                                comparisonText={
                                                    js?.filters?.Comparison ??
                                                    DEFAULT_COMPARISON
                                                }
                                            />
                                        );
                                    },
                                },

                                // Cancelled Bookings Trend
                                {
                                    type: REPORT_CHART_PERFORMANCE_VS,
                                    groupBy: [
                                        GROUP_DATE_YEAR,
                                        GROUP_DATE_MONTH,
                                        GROUP_DATE_DAY,
                                    ],
                                    props: {
                                        title: "Cancelled bookings trend",
                                        metric: "bookingsCancelled",
                                        chartType: "bar",
                                        emptyMessage:
                                            "No cancelled booking data available",
                                    },
                                },
                            ]
                          : metric === "roomNightsTotal"
                            ? [
                                  {
                                      type: CUSTOM,
                                      groupBy: [],
                                      title: "Room Nights comparison",
                                      options: { duplicable: false },
                                      render: ({ data }) => {
                                          const r = data[""]?.result ?? {};
                                          const rc =
                                              data[""]?.resultOneYearBefore ??
                                              {};
                                          return (
                                              <ReportMetricVSMetricBars
                                                  emptyMessage="No Room Night comparison data available"
                                                  data={{
                                                      CurrentPeriod: {
                                                          keys: [
                                                              [
                                                                  "Country",
                                                                  "Current period",
                                                              ],
                                                          ],
                                                          result: {
                                                              roomNightsTotal:
                                                                  r.roomNightsTotal ??
                                                                  0,
                                                          },
                                                          resultOneYearBefore: {
                                                              roomNightsTotal:
                                                                  rc.roomNightsTotal ??
                                                                  0,
                                                          },
                                                      },
                                                  }}
                                                  title="Room Nights comparison"
                                                  metrics={["roomNightsTotal"]}
                                                  aggregation="Country"
                                                  showLastYear={true}
                                                  currentSeriesLabel="Current Year"
                                                  comparisonSeriesLabel="Last Year"
                                              />
                                          );
                                      },
                                  },
                              ]
                            : []),
                ],
            };
        }),
    },
];

export const EVENTS_PERFORMANCE_REPORT_KEY = "EVENTS_PERFORMANCE";

export const getEventsPerformanceReports = ({ hotelID, js, PDF = false }) => [
    ...(PDF
        ? [
              {
                  type: CUSTOM,
                  groupBy: [],
                  title: "Title",
                  options: { duplicable: false },
                  render: () => (
                      <Typography variant="h4">Events Performance</Typography>
                  ),
              },
              {
                  type: CUSTOM,
                  groupBy: [],
                  title: "Filters",
                  options: { duplicable: false },
                  render: () => <Filters PDF={true} />,
              },
          ]
        : []),
    ...(PDF ? REPORTS.filter((r) => r.type !== BREAK_LINE) : REPORTS),
];

const Filters = ({ PDF = false }) => {
    const js = useSelector((state) => state.globalReportFilters);
    const { id: hotelID } = useSelector((state) => state.hotelID);
    const { dataSimple } = useSelector((state) => state);
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const [selectedEvent, setSelectedEvent] = useState(null);
    const [collapsedGroups, setCollapsedGroups] = useState(new Set());
    const [pendingEventSelect, setPendingEventSelect] = useState(false);

    const events = useMemo(() => {
        if (!Array.isArray(dataSimple?.data?.Events?.data)) return [];
        return dataSimple.data.Events.data;
    }, [dataSimple]);

    const sortedEvents = useMemo(() => {
        if (!events.length) return [];
        const today = dayjs().startOf("day").valueOf();
        const future = events
            .filter((e) => e.ToDate >= today)
            .sort((a, b) => a.FromDate - b.FromDate);
        const past = events
            .filter((e) => e.ToDate < today)
            .sort((a, b) => b.ToDate - a.ToDate);
        return [...future, ...past];
    }, [events]);

    // Called when user manually selects an event: only updates dates, preserves filters
    const handleEventSelect = (event) => {
        setSelectedEvent(event);
        const fromDate = dayjs(event.FromDate).format("YYYY-MM-DD");
        const toDate = dayjs(event.ToDate).format("YYYY-MM-DD");
        dispatch(
            setJS({
                ...js,
                selectedEventId: event?.EventID ?? null,
                selectedEvent: event ?? null,
                fromDay: fromDate,
                toDay: toDate,
                presetDate: PRESET_EVENT_DATES,
                fromArrivalDay: MIN_DATE.format("YYYY-MM-DD"),
                toArrivalDay: MAX_DATE.format("YYYY-MM-DD"),
                presetArrivalDate: PRESET_ANYTIME,
                fromResDateDay: MIN_DATE.format("YYYY-MM-DD"),
                toResDateDay: MAX_DATE.format("YYYY-MM-DD"),
            }),
        );
    };

    // In PDF mode, just restore the display value — no dispatching
    useEffect(() => {
        if (!PDF || !events.length) return;
        const found = js.selectedEventId
            ? events.find((e) => e.EventID === js.selectedEventId)
            : null;
        setSelectedEvent(found ?? null);
    }, [PDF, events, js.selectedEventId]);

    // Phase 1: Hotel change / screen init → reset ALL filters.
    // Signals Phase 2 to run in the next render cycle (so it reads fresh js).
    useEffect(() => {
        if (PDF) return;
        dispatch(
            setJS({
                ...js,
                dataType: DATATYPE_RESDATE,
                presetDate: PRESET_EVENT_DATES,
                presetArrivalDate: PRESET_ANYTIME,
                fromArrivalDay: MIN_DATE.format("YYYY-MM-DD"),
                toArrivalDay: MAX_DATE.format("YYYY-MM-DD"),
                fromResDateDay: MIN_DATE.format("YYYY-MM-DD"),
                toResDateDay: MAX_DATE.format("YYYY-MM-DD"),
                selectedEvent: null,
                filters: {
                    Cancellations: "included",
                    Comparison: COMPARISON_SAME_TIME_LAST_YEAR,
                    Target: TARGET_OFF,
                    filters: {},
                },
            }),
        );
        setPendingEventSelect(true);
    }, [hotelID]);

    // Phase 2: Runs in a fresh render cycle after Phase 1, so js already reflects
    // the reset filters. Picks the right event and updates only the dates.
    useEffect(() => {
        if (PDF || !pendingEventSelect) return;
        if (!events.length) {
            setSelectedEvent(null);
            return;
        }
        let auxSelectedEvent = [...events].sort(
            (a, b) => b.ToDate - a.ToDate,
        )[0];
        if (js.selectedEventId)
            auxSelectedEvent =
                events.find((e) => e.EventID === js.selectedEventId) ??
                auxSelectedEvent;

        handleEventSelect(auxSelectedEvent);
        setPendingEventSelect(false);
    }, [pendingEventSelect, events]);

    // When the user picks "Event Dates" from the TIME_PERIOD dropdown,
    // FiltersReport calls PresetDates which returns today for the unknown key.
    // This effect intercepts that and replaces the dates with the actual event range.
    useEffect(() => {
        if (js.presetDate !== PRESET_EVENT_DATES || !selectedEvent) return;
        const fromDate = dayjs(selectedEvent.FromDate).format("YYYY-MM-DD");
        const toDate = dayjs(selectedEvent.ToDate).format("YYYY-MM-DD");
        if (js.fromDay === fromDate && js.toDay === toDate) return;
        dispatch(
            setJS({
                ...js,
                fromDay: fromDate,
                toDay: toDate,
                fromArrivalDay: MIN_DATE.format("YYYY-MM-DD"),
                toArrivalDay: MAX_DATE.format("YYYY-MM-DD"),
                presetArrivalDate: PRESET_ANYTIME,
                fromResDateDay: MIN_DATE.format("YYYY-MM-DD"),
                toResDateDay: MAX_DATE.format("YYYY-MM-DD"),
            }),
        );
    }, [js.presetDate, selectedEvent]);

    return (
        <FiltersReport
            rename={{ TIME_RANGE: "" }}
            filtersOrder={[
                "CUSTOM_FILTERS",
                "DATATYPE",
                "TIME_PERIOD",
                "TIME_RANGE",
                "FILTERS",
                "CANCELLATIONS",
                "COMPARISON",
            ]}
            BookingPresetOptions={[
                PRESET_EVENT_DATES,
                PRESET_LAST_7_DAYS,
                PRESET_LAST_30_DAYS,
                PRESET_LAST_FULL_MONTH,
                PRESET_LAST_3_MONTHS,
                PRESET_LAST_6_MONTHS,
                PRESET_LAST_12_MONTHS,
                PRESET_LAST_YEAR,
                PRESET_CUSTOM,
            ]}
            showCustomTimePeriod={false}
            otherProps={{
                DATATYPE_VALUES: {
                    [DATATYPE_RESDATE]: DATATYPE_RESDATE_NAME,
                    [DATATYPE_CHECKIN]: DATATYPE_CHECKIN_NAME,
                },
            }}
            hide={[
                "FILTERS_VOUCHERS",
                "FILTERS_EXTRAS",
                "ARRIVAL_PERIOD",
                "TIME_ARRIVAL_RANGE",
                "RESDATE_PERIOD",
                "TIME_BOOKING_RANGE",
                "FILTERS_TYPES",
            ]}
            PDF={PDF}
            {...(!PDF && {
                defaultValues: {
                    dataType: DATATYPE_RESDATE,
                },
                onPDFExport: () => {
                    dispatch(setTemplateKey(EVENTS_PERFORMANCE_REPORT_KEY));
                    navigate("/pdfexport");
                },
            })}
            CustomFilters={() => (
                <>
                    <Typography>Select event</Typography>
                    <FormControl>
                        <Autocomplete
                            multiple={false}
                            disableClearable
                            options={sortedEvents}
                            groupBy={(option) =>
                                option.ToDate >=
                                dayjs().startOf("day").valueOf()
                                    ? "Future"
                                    : "Past"
                            }
                            noOptionsText={"No events available"}
                            renderGroup={(params) => {
                                const isCollapsed = collapsedGroups.has(
                                    params.group,
                                );
                                return (
                                    <li key={params.key}>
                                        <Box
                                            onClick={() =>
                                                setCollapsedGroups((prev) => {
                                                    const next = new Set(prev);
                                                    if (next.has(params.group))
                                                        next.delete(
                                                            params.group,
                                                        );
                                                    else next.add(params.group);
                                                    return next;
                                                })
                                            }
                                            sx={{
                                                cursor: "pointer",
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "space-between",
                                                px: 2,
                                                py: 0.5,
                                                fontWeight: "bold",
                                                fontSize: "0.75rem",
                                                userSelect: "none",
                                                color: "text.secondary",
                                            }}
                                        >
                                            <span>{params.group}</span>
                                            {isCollapsed ? (
                                                <Icons.ArrowUp />
                                            ) : (
                                                <Icons.ArrowDown />
                                            )}
                                        </Box>
                                        <div
                                            style={{
                                                display: isCollapsed
                                                    ? "none"
                                                    : "block",
                                            }}
                                        >
                                            {params.children}
                                        </div>
                                    </li>
                                );
                            }}
                            renderOption={(props, option) => (
                                <li {...props} key={option.EventID}>
                                    {option.Name}
                                </li>
                            )}
                            getOptionLabel={(option) => option.Name}
                            value={selectedEvent}
                            onChange={(ev, newValue) => {
                                handleEventSelect(newValue);
                            }}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    placeholder={
                                        sortedEvents.length === 0
                                            ? "No events available"
                                            : "Select an event"
                                    }
                                    variant="outlined"
                                    size="small"
                                    sx={{
                                        "& .MuiInputBase-root": {
                                            height: ITEM_HEIGHT,
                                        },
                                    }}
                                />
                            )}
                            sx={{ width: "250px" }}
                        />
                    </FormControl>
                </>
            )}
        />
    );
};

const EventsPerformanceScreen = () => {
    const js = useSelector((state) => state.globalReportFilters);
    const { id: hotelID } = useSelector((state) => state.hotelID);
    const authRoles = useSelector((state) => state.authRoles.edited);
    const { dataSimple } = useSelector((state) => state);
    const reports = getEventsPerformanceReports({ hotelID, js });

    const [showPrediction, setShowPrediction] = useState(false);

    const isAdmin = ARO_ADMIN.some((role) => authRoles.includes(role));

    const selectedEvent = useMemo(() => {
        if (
            !Array.isArray(dataSimple?.data?.Events?.data) ||
            !js.selectedEventId
        )
            return null;
        return (
            dataSimple.data.Events.data.find(
                (e) => e.EventID === js.selectedEventId,
            ) ?? null
        );
    }, [dataSimple, js.selectedEventId]);

    const isFutureEvent = selectedEvent
        ? selectedEvent.ToDate >= dayjs().startOf("day").valueOf()
        : false;

    return (
        <>
            {showPrediction && (
                <EventPredictionAI
                    selectedEvent={selectedEvent}
                    onBack={() => setShowPrediction(false)}
                />
            )}

            <Container
                maxWidth={false}
                sx={{ display: showPrediction ? "none" : undefined }}
            >
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        mt: 2,
                        mb: 2,
                    }}
                >
                    <Typography variant="h4">Events Performance</Typography>
                    {isAdmin && isFutureEvent && (
                        <Button
                            variant="contained"
                            startIcon={<Icons.AI />}
                            onClick={() => setShowPrediction(true)}
                            sx={{ textTransform: "none", color: "white" }}
                        >
                            Event Prediction - AI
                        </Button>
                    )}
                </Box>
                <Typography variant="subtitle1" color="initial">
                    This report visualizes the performance of key metrics based
                    on the dates of an existing event.
                </Typography>
                <br />
                <Filters />
                {!showPrediction && <ReportBuilder reports={reports} />}
            </Container>
        </>
    );
};

export default EventsPerformanceScreen;
